import { useCallback, useEffect, useState } from 'react';
import {
  CareerAdvisorAvailability,
  getCareerAdvisorAvailability,
} from '../../services/career-advisor.service';
import { AssessmentLooksPreview } from './AssessmentLooksPreview';
import { CareerAdvisorBlock } from './career-advisor/CareerAdvisorBlock';
import { InterviewConsentGate } from './InterviewConsentGate';

interface InterviewPreparationConsentProps {
  invitationToken: string;
  onConsentChange: (hasConsented: boolean) => void;
}

/**
 * Prep-page consent area inside Security & Evaluation Protocol.
 *
 * Render order:
 * 1. How the Assessment Looks — always
 * 2. Interview Coach — only when the agent is enabled
 * 3. Consent checkbox / start gate — always, after the coach
 */
export const InterviewPreparationConsent = ({
  invitationToken,
  onConsentChange,
}: InterviewPreparationConsentProps) => {
  const [availability, setAvailability] =
    useState<CareerAdvisorAvailability | null>(null);
  const [loaded, setLoaded] = useState(false);

  const refreshAvailability = useCallback(async () => {
    const response = await getCareerAdvisorAvailability(invitationToken);
    setAvailability(response.data ?? null);
  }, [invitationToken]);

  useEffect(() => {
    let active = true;
    getCareerAdvisorAvailability(invitationToken)
      .then((response) => {
        if (active) setAvailability(response.data ?? null);
      })
      .finally(() => {
        if (active) setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [invitationToken]);

  const isCoachEnabled = Boolean(availability?.enabled);

  return (
    <div className="w-full space-y-8">
      <AssessmentLooksPreview />

      {!loaded ? (
        <div className="w-full h-24 rounded-2xl bg-gray-100 animate-pulse" />
      ) : isCoachEnabled ? (
        <CareerAdvisorBlock
          invitationToken={invitationToken}
          availability={availability!}
          onSessionFinished={refreshAvailability}
        />
      ) : null}

      <InterviewConsentGate onConsentChange={onConsentChange} />
    </div>
  );
};
